import { ScopeSectionRuleModeName, type ScopeSectionRuleTypeName } from '@ydistri/api-sdk';
import React from 'react';
import AddScopeSection from './AddScopeSection';
import ScopeSection from './ScopeSection';
import type { ScopeRuleDefinition, ScopeSectionDraft } from './scopeEditorModel';
import type { ScopeRuleMultiselectEntity } from './scopeActiveEntityOptions';
import {
  RuleSetGuidanceList,
  RuleSets,
  RuleSetsFooter,
  RuleSetWarning,
  RulesSectionContainer,
} from './ScopeSectionsStyledComponents';
import useScopeSections from './useScopeSections';

export interface ScopeSectionsProps {
  footerContent?: React.ReactNode;
  getRuleMultiselectData: (
    type: ScopeSectionRuleTypeName,
    selectedValueIds?: number[],
  ) => ScopeRuleMultiselectEntity[];
  isLoading?: boolean;
  readOnly?: boolean;
  ruleDefinitions: ScopeRuleDefinition[];
  ruleMessagesById?: Record<string, string[]>;
  sectionIdsWithWarnings?: string[];
  sections: ScopeSectionDraft[];
  onSectionsChange: (sections: ScopeSectionDraft[]) => void;
}

const ScopeSections: React.FC<ScopeSectionsProps> = ({
  footerContent,
  getRuleMultiselectData,
  isLoading = false,
  readOnly = false,
  ruleDefinitions,
  ruleMessagesById = {},
  sectionIdsWithWarnings = [],
  sections,
  onSectionsChange,
}) => {
  const { addSection, removeSection, updateSectionRules } = useScopeSections({
    onSectionsChange,
    sections,
  });
  const hasMultipleSections = sections.length > 1;
  const hasExcludedRule = sections.some(section =>
    section.rules.some(rule => rule.mode === ScopeSectionRuleModeName.Exclude),
  );

  return (
    <RulesSectionContainer>
      <RuleSets>
        {sections.map((section, index) => (
          <ScopeSection
            getRuleMultiselectData={getRuleMultiselectData}
            index={index}
            hasWarning={sectionIdsWithWarnings.includes(section.id)}
            isLoading={isLoading}
            key={section.id}
            readOnly={readOnly}
            ruleDefinitions={ruleDefinitions}
            ruleMessagesById={ruleMessagesById}
            section={section}
            onRemove={() => removeSection(section.id)}
            onRulesChange={rules => updateSectionRules(section.id, rules)}
          />
        ))}
        {hasMultipleSections && (
          <RuleSetGuidanceList aria-label="Rule set guidance">
            <li>Rule set SKUs are merged with UNION, added together.</li>
            {hasExcludedRule && (
              <RuleSetWarning>
                Warning: SKUs excluded from one Rule set can be still available in the second Rule
                set - if needed, exclude them from both Rule sets!
              </RuleSetWarning>
            )}
          </RuleSetGuidanceList>
        )}
        <RuleSetsFooter $hasContent={footerContent !== undefined}>
          {footerContent}
          <AddScopeSection disabled={readOnly} onAdd={addSection} />
        </RuleSetsFooter>
      </RuleSets>
    </RulesSectionContainer>
  );
};

export default ScopeSections;
